import { useState } from 'react';
import { Alert } from '@material-ui/lab';
import { Button } from '@material-ui/core';

import {
  UserTag,
  PhoneCallBtn,
  SupportWebChatButton,
  UserTagDescription,
} from './components';
import { ChipOrderDisclaimer } from 'components/order-management/components/order-mgmt-dialog/components';

import { useOrderManagement } from '../order-management/hooks';

import useStyles from './OrderDetailDialogCustomerBox.styles';

function OrderDetailDialogCustomerBox(props) {
  const {
    alertMessage,
    billTypeLabel,
    headerTitle,
    emailLabel,
    showAddress,
    showBillAlert,
    showBillType,
    showEmail,
    showMapButton,
    showMapButtonText,
    showPhoneBtn,
    showPropioType,
    showTypeDescription,
    showTypeOwner,
    user = {},
    fullNameText,
    zipCodeLabel,
    neighborhoodLabel,
    showSupportWebChatButton,
    callUserDisclaimer,
    tryACallDisclaimer,
  } = props;

  const showFooter = showSupportWebChatButton || showPhoneBtn;
  const showHighValueUserType = user?.high_value_user ?? false;
  const showIsNewUser = user?.is_new_user ?? false;

  const [beingHoveredVIPChip, setBeingHoveredVIPChip] = useState(false);
  const [beingHoveredNewUserChip, setBeingHoveredNewUserChip] = useState(false);
  const [beingHoveredPropioChip, setBeingHoveredPropioChip] = useState(false);

  const {
    onOpenSupportWebChat,
    hasNoUserResponse,
    isOrderWaiting,
    isOrderTimingOut,
  } = useOrderManagement();

  const handleMapBtn = () =>
    window.open(
      `https://www.google.com/maps?q=${user.address_lat},${user.address_lng}`,
      '_blank',
    );

  const handleOnMouseOver = (event, callback) => {
    event.preventDefault();
    callback(true);
  };

  const handleOnMouseLeave = (event, callback) => {
    event.preventDefault();
    callback(false);
  };

  const userTags = [
    {
      enabled: showIsNewUser,
      variant: 'new',
      onMouseOver: e => handleOnMouseOver(e, setBeingHoveredNewUserChip),
      onMouseLeave: e => handleOnMouseLeave(e, setBeingHoveredNewUserChip),
    },
    {
      enabled: showHighValueUserType,
      variant: 'pro',
      onMouseOver: e => handleOnMouseOver(e, setBeingHoveredVIPChip),
      onMouseLeave: e => handleOnMouseLeave(e, setBeingHoveredVIPChip),
    },
    {
      enabled: showPropioType,
      variant: 'own',
      onMouseOver: e => handleOnMouseOver(e, setBeingHoveredPropioChip),
      onMouseLeave: e => handleOnMouseLeave(e, setBeingHoveredPropioChip),
    },
  ];

  const userTagDescriptions = [
    { userTag: 'own', show: beingHoveredPropioChip },
    { userTag: 'new', show: beingHoveredNewUserChip },
    { userTag: 'pro', show: beingHoveredVIPChip },
  ].filter(desc => desc.show);

  const _classes = useStyles({ showFooter });

  return (
    <article aria-label='customer-box' className={_classes.root}>
      <header className={_classes.header}>
        <h1 className={_classes.headerTitle}>{headerTitle}</h1>
      </header>

      <main className={_classes.main}>
        <section aria-label='user-info' className={_classes.userInfo}>
          <p className={_classes.userInfoName}>{user.basicName}</p>
          <div className={_classes.userTags}>
            {userTags.map(tag =>
              !tag.enabled ? null : (
                <UserTag
                  key={tag.variant}
                  variant={tag.variant}
                  onMouseOver={tag.onMouseOver}
                  onMouseLeave={tag.onMouseLeave}
                />
              ),
            )}
          </div>
        </section>

        <aside
          aria-label='user-tag-description'
          hidden={userTagDescriptions.length === 0}
        >
          {userTagDescriptions.map(desc =>
            !desc.show ? null : (
              <UserTagDescription key={desc.userTag} userTag={desc.userTag} />
            ),
          )}
        </aside>

        <aside aria-label='order-disclaimer'>
          {/* TODO: does anyone know how it is activated? */}
          {hasNoUserResponse ||
            isOrderWaiting ||
            (isOrderTimingOut && (
              <ChipOrderDisclaimer
                classes={{
                  disclaimer: _classes.callUserDisclaimer,
                  icon: _classes.callUserDisclaimerIcon,
                  text: _classes.callUserDisclaimerText,
                }}
                type='warning'
                disclaimer={
                  isOrderTimingOut ? tryACallDisclaimer : callUserDisclaimer
                }
                hasIcon
              />
            ))}
        </aside>

        <section aria-label='contact-info' className={_classes.contactInfo}>
          <div
            hidden={showPhoneBtn && !showAddress}
            className={_classes.contactInfoData}
          >
            <p hidden={showPhoneBtn} className={_classes.contactInfoPhone}>
              {`Tel: ${user.phone}`}
            </p>
            <div
              hidden={!showAddress}
              className={_classes.contactInfoDirection}
            >
              <p>{user.address}</p>
              <p>{user.address_description}</p>
              <p hidden={!Boolean(user?.zip_code)}>
                {`${zipCodeLabel}: ${user.zip_code}`}
              </p>
              <p hidden={!Boolean(user?.neighborhood)}>
                {`${neighborhoodLabel}: ${user?.neighborhood}`}
              </p>
            </div>
          </div>
          {showMapButton && (
            <Button
              variant='text'
              onClick={handleMapBtn}
              classes={{ root: _classes.contactInfoMapBtn }}
            >
              {showMapButtonText}
            </Button>
          )}
        </section>

        <section aria-label='emit-bill' hidden={!showBillAlert}>
          <Alert
            severity='info'
            variant='standard'
            classes={{ standardInfo: _classes.billAlert }}
          >
            {alertMessage}
          </Alert>
          <div className={_classes.emitBillData}>
            <p hidden={!showEmail}>
              {emailLabel} {user.email}
            </p>
            {showBillType && (
              <>
                <p>
                  {billTypeLabel} {user?.bill_type}
                </p>
                {showTypeOwner && (
                  <p>
                    {user?.document_type_owner_label}:&ensp;
                    {user?.document_type_owner_value}
                  </p>
                )}
                <p>{fullNameText}</p>
                {showTypeDescription && (
                  <p>
                    {user?.document_type_description_label}:&ensp;
                    {user?.document_type_description_value}
                  </p>
                )}
              </>
            )}
          </div>
        </section>
      </main>

      <footer className={_classes.footer}>
        {showSupportWebChatButton && (
          <SupportWebChatButton
            showShortText={showPhoneBtn}
            onClick={onOpenSupportWebChat}
          />
        )}
        {showPhoneBtn && <PhoneCallBtn />}
      </footer>
    </article>
  );
}

export default OrderDetailDialogCustomerBox;
